import { useCallback, useEffect, useState } from "react";
import { Download, LogOut, RefreshCw } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { adminGet, adminDownload, getToken, clearToken } from "@/lib/adminApi";
import AdminLogin from "@/components/admin/AdminLogin";
import AdminStats from "@/components/admin/AdminStats";
import { DataTable, orderColumns, subscriptionColumns, signupColumns } from "@/components/admin/AdminTables";

const TABS = [
  { id: "orders", label: "Orders", columns: orderColumns, key: "session_id", filters: ["all", "paid", "pending"] },
  { id: "subscriptions", label: "Subscriptions", columns: subscriptionColumns, key: "subscription_id", filters: ["all", "active", "pending"] },
  { id: "signups", label: "Email Signups", columns: signupColumns, key: "email", filters: [] },
];

function Toolbar({ tab, filter, setFilter, onRefresh, onExport }) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      {tab.filters.map((f) => (
        <button key={f} onClick={() => setFilter(f)} className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${filter === f ? "bg-gold text-white border-gold" : "border-gold/25 text-gray-500 hover:border-gold"}`} data-testid={`admin-filter-${f}`}>
          {f[0].toUpperCase() + f.slice(1)}
        </button>
      ))}
      <span className="flex-1" />
      <button onClick={onRefresh} className="text-xs font-semibold text-gray-500 hover:text-gold flex items-center gap-1" data-testid="admin-refresh-btn"><RefreshCw size={14} /> Refresh</button>
      <button onClick={onExport} className="text-xs font-semibold text-brown-dark border border-gold/25 hover:border-gold rounded-full px-3 py-1.5 flex items-center gap-1" data-testid="admin-export-btn"><Download size={14} /> Export CSV</button>
    </div>
  );
}

export default function Admin() {
  useSEO({ title: "Owner Dashboard | Ken's Gold", description: "Private orders dashboard." });
  const [authed, setAuthed] = useState(!!getToken());
  const [tabId, setTabId] = useState("orders");
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const onUnauthorized = useCallback(() => setAuthed(false), []);
  const tab = TABS.find((t) => t.id === tabId);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const query = tab.filters.length ? `?status=${filter}` : "";
      const [data, sum] = await Promise.all([adminGet(`/${tab.id}${query}`), adminGet("/summary")]);
      setRows(data);
      setSummary(sum);
    } catch (e) {
      if (e.message === "unauthorized") setAuthed(false);
      else setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tab, filter]);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;

  const logout = () => {
    clearToken();
    setAuthed(false);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap gap-4 items-center justify-between mb-8">
        <div>
          <p className="text-xs font-bold tracking-widest text-gold uppercase mb-1">Private</p>
          <h1 className="font-playfair text-3xl md:text-4xl font-bold text-brown-dark">Owner Dashboard</h1>
        </div>
        <button onClick={logout} className="text-sm text-gray-500 hover:text-brown-dark flex items-center gap-1" data-testid="admin-logout-btn"><LogOut size={16} /> Sign out</button>
      </div>
      <AdminStats summary={summary} />
      <div className="flex flex-wrap gap-1 border-b border-gold/15 mb-5">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => { setTabId(t.id); setFilter("all"); }} className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tabId === t.id ? "border-gold text-brown-dark" : "border-transparent text-gray-400 hover:text-brown-dark"}`} data-testid={`admin-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>
      {error && <p role="alert" className="text-sm text-red-600 mb-4" data-testid="admin-dashboard-error">{error}</p>}
      <Toolbar tab={tab} filter={filter} setFilter={setFilter} onRefresh={load} onExport={() => adminDownload(tab.id).catch((e) => setError(e.message))} />
      {loading && rows.length === 0 ? (
        <p className="text-sm text-gray-400 py-10 text-center" data-testid="admin-loading">Loading...</p>
      ) : (
        <DataTable columns={tab.columns} rows={rows} rowKey={tab.key} emptyText={`No ${tab.label.toLowerCase()} yet.`} testId={`admin-table-${tab.id}`} />
      )}
    </div>
  );
}
